import { lowestPlanPrice, highestPlanPrice } from "@/data/pricing";
import { LastUpdated } from "@/components/site/LastUpdated";
import { applyPageMetadata } from "@/lib/page-metadata";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout, Section, Eyebrow, CTAButton, BOOKING_URL } from "@/components/site/Layout";
import { CaseStudy, serviceCaseStudy } from "@/components/site/CaseStudy";
import { TrustSignals } from "@/components/site/TrustSignals";
import { FAQSection } from "@/components/site/FAQSection";
import { HeroLeadForm } from "@/components/site/HeroLeadForm";
import { PricingTeaser } from "@/components/site/Pricing";
import BacklinkRiskCheck from "@/components/blog/BacklinkRiskCheck";
import { Check } from "lucide-react";
import { services } from "@/data/services";
import { jsonLdScript, ogImageFor } from "@/lib/seo";

const TITLE = "Link Building for Law Firms | Attorney Link Building Services | PageOne Lawyers";
const DESCRIPTION =
  "Link building for law firms: 4 to 12 editorial placements a month on DR 50+ sites, plus local and legal links. No link schemes, every link reported.";
const URL = "https://pageonelawyers.com/services/link-building-for-law-firms";
const OG_IMAGE = ogImageFor("service", "link-building");
const SCHEMAS = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://pageonelawyers.com/services/link-building-for-law-firms#webpage",
    url: "https://pageonelawyers.com/services/link-building-for-law-firms",
    name: "Link Building for Law Firms | PageOne Lawyers",
    description:
      "Link building for law firms: 4 to 12 editorial placements a month on DR 50+ sites, plus local and legal links. No link schemes, every link reported.",
    inLanguage: "en",
    isPartOf: { "@type": "WebSite", name: "PageOne Lawyers", url: "https://pageonelawyers.com/" },
    breadcrumb: {
      "@id": "https://pageonelawyers.com/services/link-building-for-law-firms#breadcrumb",
    },
    mainEntity: {
      "@id": "https://pageonelawyers.com/services/link-building-for-law-firms#service",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://pageonelawyers.com/services/link-building-for-law-firms#service",
    name: "Link Building for Law Firms",
    serviceType: "Link building",
    description:
      "Link building for law firms and lawyers: editorial placements, digital PR, local news and community links, bar association and alumni placements, resource page outreach, unlinked brand mentions, legal directory citations and toxic link clean-up, with every link reported.",
    url: "https://pageonelawyers.com/services/link-building-for-law-firms",
    provider: {
      "@type": "Organization",
      name: "PageOne Lawyers",
      url: "https://pageonelawyers.com/",
    },
    audience: { "@type": "BusinessAudience", audienceType: "Law firms and attorneys" },
    areaServed: { "@type": "Country", name: "United States" },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: lowestPlanPrice,
      highPrice: highestPlanPrice,
      offerCount: "6",
      url: "https://pageonelawyers.com/pricing",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": "https://pageonelawyers.com/services/link-building-for-law-firms#breadcrumb",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://pageonelawyers.com/" },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: "https://pageonelawyers.com/services",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Link Building for Law Firms",
        item: "https://pageonelawyers.com/services/link-building-for-law-firms",
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://pageonelawyers.com/services/link-building-for-law-firms#faq",
    mainEntity: [
      {
        "@type": "Question",
        name: "How many links will we get each month?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We secure 4 to 12 editorial placements a month on sites rated DR 50 or higher, depending on your plan and market, alongside local links and citations.",
        },
      },
      {
        "@type": "Question",
        name: "What makes a good backlink for a law firm?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A link from a site your clients or peers would actually trust, on a page related to your practice, placed where a reader would click it. Relevance and trust matter more than raw numbers. A handful of strong lawyer backlinks will do more than hundreds of weak ones.",
        },
      },
      {
        "@type": "Question",
        name: "Do you use private blog networks or link schemes?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. We don't use private blog networks, automated link programs, link exchanges or bulk directory packages. Google's spam policies target all of them, and the risk falls on your firm.",
        },
      },
      {
        "@type": "Question",
        name: "Will we see every link you build?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Your monthly report lists every new link with its URL, date, anchor text and the page it points to, so you can check each one yourself.",
        },
      },
      {
        "@type": "Question",
        name: "What happens if a link is removed?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We replace it. Sites change and pages come down, so replacing lost placements is part of the service.",
        },
      },
      {
        "@type": "Question",
        name: "Can you clean up bad links from a previous agency?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. We audit your backlink profile, ask for harmful links to be removed where possible, and prepare a disavow file for the rest.",
        },
      },
      {
        "@type": "Question",
        name: "How long before links make a difference?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Most firms see movement over several months, as new links are found and counted. Competitive markets and practice areas take longer.",
        },
      },
      {
        "@type": "Question",
        name: "Do you build local links?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Local news, community organizations, sponsorships and local directories are a core part of every campaign, because they support your map rankings as well as your website.",
        },
      },
      {
        "@type": "Question",
        name: "Is link building included in your SEO plans?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Link building is included in every SEO plan, scaled to your plan and markets. You can see every plan on our pricing page.",
        },
      },
    ],
  },
];
const FAQS = [
  {
    q: "How many links will we get each month?",
    a: "We secure 4 to 12 editorial placements a month on sites rated DR 50 or higher, depending on your plan and market, alongside local links and citations.",
  },
  {
    q: "What makes a good backlink for a law firm?",
    a: "A link from a site your clients or peers would actually trust, on a page related to your practice, placed where a reader would click it. Relevance and trust matter more than raw numbers. A handful of strong lawyer backlinks will do more than hundreds of weak ones.",
  },
  {
    q: "Do you use private blog networks or link schemes?",
    a: "No. We don't use private blog networks, automated link programs, link exchanges or bulk directory packages. Google's spam policies target all of them, and the risk falls on your firm.",
  },
  {
    q: "Will we see every link you build?",
    a: "Yes. Your monthly report lists every new link with its URL, date, anchor text and the page it points to, so you can check each one yourself.",
  },
  {
    q: "What happens if a link is removed?",
    a: "We replace it. Sites change and pages come down, so replacing lost placements is part of the service.",
  },
  {
    q: "Can you clean up bad links from a previous agency?",
    a: "Yes. We audit your backlink profile, ask for harmful links to be removed where possible, and prepare a disavow file for the rest.",
  },
  {
    q: "How long before links make a difference?",
    a: "Most firms see movement over several months, as new links are found and counted. Competitive markets and practice areas take longer.",
  },
  {
    q: "Do you build local links?",
    a: "Yes. Local news, community organizations, sponsorships and local directories are a core part of every campaign, because they support your map rankings as well as your website.",
  },
  {
    q: "Is link building included in your SEO plans?",
    a: "Yes. Link building is included in every SEO plan, scaled to your plan and markets. You can see every plan on our pricing page.",
  },
];

export const Route = createFileRoute("/services/link-building-for-law-firms")({
  head: () => applyPageMetadata("/services/link-building-for-law-firms", {
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:type", content: "image/svg+xml" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Legal Link Building — PageOne Lawyers" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: SCHEMAS.map(jsonLdScript),
  }),
  component: LinkBuildingPage,
});
function LinkBuildingPage() {
  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-primary-foreground/70">
            <ol className="flex flex-wrap gap-1">
              <li>
                <Link to="/">Home</Link> /
              </li>
              <li>
                <Link to="/services">Services</Link> /
              </li>
              <li>Link Building for Law Firms</li>
            </ol>
          </nav>
          <Eyebrow>
            <span className="text-gold">Service</span>
          </Eyebrow>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-semibold leading-tight sm:text-5xl md:text-6xl">
            {"Link Building for Law Firms and Lawyers"}
          </h1><LastUpdated />
          <p className="mt-6 max-w-3xl text-xl text-primary-foreground/85">
            {
              "Attorney link building services that earn links from legal publications, local news and the organizations your clients already trust, with every link reported."
            }
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <CTAButton to="#free-plan" variant="gold">
              Get a free backlink review
            </CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">
              Talk to a strategist
            </CTAButton>
          </div>
        </div>
      </section>
      <Section>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "Link building for law firms works when every link would make sense even if search engines didn't exist: a bar association profile, a local news quote, a legal publication citing your attorney. That's the kind of link building for lawyers we do. We earn 4 to 12 editorial placements a month on sites rated DR 50 or higher, point them at the pages that bring you cases, and show you every single link in your monthly report."
          }
        </p>
      </Section>
      <div className="border-y border-border bg-secondary/40">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"What's Included in Our Link Building for Law Firms"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Our attorney link building services combine editorial outreach, PR and local relationships, so your link profile looks like a real firm's, not a campaign's."
            }
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">
                {"Editorial placements on authoritative sites"}
              </h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "Each month we secure 4 to 12 editorial placements on sites rated DR 50 or higher, including legal publications and respected sites outside law, such as finance, insurance and real estate, where a legal view genuinely helps their readers."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Digital PR for law firms"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "We turn your attorneys' knowledge into stories journalists want: commentary on new laws and court decisions, local data and expert quotes. As a digital PR agency for law firms, we pitch where your clients actually read."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Local news and community links"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "Local newspapers, community organizations, chambers of commerce and sponsorships you already support. These links tell Google where you practice as clearly as your address does."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Bar association and alumni placements"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "Profiles and mentions on bar association, law school alumni and professional association sites, which few competitors can copy."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Resource page and broken link outreach"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "We find pages that list legal resources for the public, and broken links that once pointed to similar content, and offer your best guides as the replacement."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Unlinked brand mentions"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "When your firm or attorneys are mentioned online without a link, we ask the publisher to add one. These are often the easiest high-quality links to win."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">
                {"Advanced citation and link building for law firms"}
              </h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "Accurate listings on the legal directories that matter, such as Avvo, Justia, FindLaw, Martindale and your state bar, plus the main business data sources. Consistent citations support both your local rankings and your link profile."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Toxic link audit and disavow"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "If a previous agency left you with spammy links, we audit your backlink profile, remove what we can and prepare a disavow file for the links that can't be removed."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Every link reported"}</h3>
              <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
                {
                  "Your monthly report lists every new link: the URL, the date, the anchor text and the page it points to. If a placement is removed, we replace it."
                }
              </p>
            </article>
          </div>
        </Section>
      </div>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">{"How We Earn Each Link"}</h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "Law firm link building is slow, careful work, and good link building for attorneys can't be rushed. Every link goes through the same steps, so you always know where it came from and why it's there."
          }
        </p>
        <figure className="mt-8 overflow-hidden rounded-lg border border-border bg-background">
          <img
            src="/images/services/link-building-for-law-firms/how-a-link-is-earned.svg"
            alt="Six steps to earn a law firm backlink: audit the backlink profile and competitors, choose the pages that need links, find real opportunities, pitch and create, verify the live link, then report and replace if removed."
            width="1200"
            height="500"
            loading="lazy"
            decoding="async"
            className="h-auto w-full"
          />
          <figcaption className="border-t border-border px-5 py-3 text-sm text-muted-foreground">
            {
              "Every link is researched, earned, checked and reported, and replaced if it disappears."
            }
          </figcaption>
        </figure>
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Audit your profile and your competitors"}</h3>
            <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
              {
                "We review your existing backlinks and the links behind the firms that outrank you, so we know what's missing and what's risky."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Choose the pages that need links"}</h3>
            <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
              {
                "Links go to the practice pages and guides that bring in cases, not just your homepage."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Find real opportunities"}</h3>
            <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
              {
                "Publications, local organizations and resource pages where a link from your firm makes sense to their readers."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Pitch and create"}</h3>
            <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
              {
                "We write the pitch, the commentary or the guest article, and your attorney approves anything published under their name."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Verify the live link"}</h3>
            <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
              {"We check that each link is live, indexable and points to the right page."}
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Report and replace"}</h3>
            <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
              {
                "Every link goes into your monthly report. If a placement is removed, we replace it."
              }
            </p>
          </article>
        </div>
      </Section>
      <div className="border-y border-border bg-secondary/40">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"Backlinks for Lawyers: What We Build and What We Never Build"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Google's spam policies define link spam as links created mainly to manipulate rankings, and they list examples such as automated link programs, excessive link exchanges and low-quality directory links. A single bad campaign can undo years of good work, so we stay well clear of anything on that list."
            }
          </p>
          <figure className="mt-8 overflow-hidden rounded-lg border border-border bg-background">
            <img
              src="/images/services/link-building-for-law-firms/link-types-build-vs-never.svg"
              alt="Two columns comparing the law firm backlinks we build, such as editorial placements, local news, bar associations and legal directories, with the link types we never build, such as private blog networks, automated links, link exchanges and bulk directory packages."
              width="1200"
              height="600"
              loading="lazy"
              decoding="async"
              className="h-auto w-full"
            />
            <figcaption className="border-t border-border px-5 py-3 text-sm text-muted-foreground">
              {"Every link we build would make sense even if search engines didn't exist."}
            </figcaption>
          </figure>
        </Section>
      </div>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
          {"Check Your Backlink Risk"}
        </h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "Worked with an SEO agency before? Answer eight quick questions about your firm's past link building to see whether your profile needs a clean-up before new links are built."
          }
        </p>
        <BacklinkRiskCheck />
      </Section>
      <section id="free-plan" className="scroll-mt-24 bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-6 sm:py-20">
          <HeroLeadForm
            layout="split"
            headingLevel="h2"
            eyebrow={"FREE BACKLINK REVIEW"}
            heading={"Get a free review of your firm's backlinks."}
            intro={
              "Send us your website and we'll come back within 24 hours with what's helping, what's holding you back and where your next links should come from."
            }
            sideContent={
              <>
                <p className="mt-7 text-sm font-bold">What we'll look at for your firm:</p>
                <ul className="mt-4 space-y-4 text-sm leading-relaxed text-primary-foreground/80">
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"The links you already have, and any that could be putting you at risk"}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"The links your top competitors have that you don't"}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"Which of your pages need links most to bring in cases"}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"Local and legal sites where your firm should already be listed"}
                    </span>
                  </li>
                </ul>
                <p className="mt-6 text-xs text-primary-foreground/60">
                  Prefer to talk first?{" "}
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold underline underline-offset-4"
                  >
                    Book a strategy call
                  </a>
                </p>
              </>
            }
          />
        </div>
      </section>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
          {"Link Building for Attorneys in Every Practice Area"}
        </h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "The best link opportunities depend on the work you do. A family law firm earns links from parenting and community resources; a criminal defense firm from local news on court decisions and legal commentary. We plan link building for legal sites around the practice areas you want to grow."
          }
        </p>
        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "family-law-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"link building for family law firms"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "criminal-defense-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"effective backlink strategies for criminal defense lawyers"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "personal-injury-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"personal injury lawyer SEO"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "estate-planning-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"estate planning lawyer SEO"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas"
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"all practice areas"}
            </Link>
          </li>
        </ul>
      </Section>
      <PricingTeaser
        title={"Link Building Services for Lawyers, Included in Every SEO Plan"}
        sub={
          "Our link building services for lawyers, including all law link building services listed above, are part of every SEO plan, so links are always built to support the pages and keywords in your wider strategy. The number of placements scales with your plan, your markets and how competitive your practice areas are."
        }
      />
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
          {"Why Firms Choose PageOne for Attorney Link Building"}
        </h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "Lawyer link building is all we do on the link side, and we only work with law firms, so we know which publications, associations and local sites carry weight in legal search. Our law firm backlinks are earned, not bought in bulk, and we never use private blog networks, automated link programs or directory packages. You see every link we build, and if one is removed, we replace it. That's what SEO link building services for lawyers and attorneys should look like."
          }
        </p>
      </Section>
      <CaseStudy {...serviceCaseStudy("Legal Link Building")} semanticSubheadings />
      <TrustSignals
        semanticSubheadings
        intro="Every legal link building engagement is delivered by the same senior team, on the same guarantees, with reporting tied to signed cases — not clicks."
      />
      <FAQSection
        tone="muted"
        title={"Link Building for Lawyers: Questions Firms Ask"}
        items={FAQS}
        semanticQuestionHeadings
      />
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-5xl px-5 py-14 text-center sm:px-6 sm:py-20">
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"Ready for Links That Bring In Cases?"}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-primary-foreground/80">
            {
              "Get a free review of your current backlinks, with a clear plan for where your next links should come from."
            }
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
            <CTAButton to="#free-plan" variant="gold">
              Get my free backlink review
            </CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">
              Book a strategy call
            </CTAButton>
          </div>
        </div>
      </section>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">{"Related Services"}</h2>
        <ul className="mt-6 flex flex-wrap gap-2">
          {services
            .filter((item) => item.slug !== "link-building-for-law-firms")
            .map((item) => (
              <li key={item.slug}>
                <Link
                  to="/services/$slug"
                  params={{ slug: item.slug }}
                  className="inline-block rounded-full border border-border px-4 py-2 text-sm transition hover:border-gold hover:text-gold-deep"
                >
                  {item.name}
                </Link>
              </li>
            ))}
        </ul>
      </Section>
    </Layout>
  );
}
